import { defaultRangeExtractor, useVirtualizer, type Range, type VirtualItem } from '@tanstack/react-virtual';
import { useCallback, useRef, useState, type KeyboardEventHandler, type RefObject } from 'react';
import type { VisibleTreeRow } from './useTreeSelectRendering';

export type TreeSelectVirtualizationState =
  | {
      shouldVirtualize: false;
      virtualItems: readonly [];
    }
  | {
      shouldVirtualize: true;
      virtualItems: readonly VirtualItem[];
      totalSize: number;
    };

export type TreeSelectRowControls = {
  tabbableIndex: number;
  handleVirtualListKeyDown: KeyboardEventHandler<HTMLElement>;
  handleVirtualRowFocus: (index: number) => void;
  registerVirtualRow: (index: number, node: Element | null) => void;
};

type TreeSelectVirtualization = TreeSelectVirtualizationState &
  TreeSelectRowControls & {
    parentRef: RefObject<HTMLElement | null>;
  };

const VIRTUALIZATION_THRESHOLD = 80;
const EMPTY_VIRTUAL_ITEMS: readonly [] = [];

type TreeNavigationKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

export function getNextTreeRowIndex(
  visibleRows: readonly VisibleTreeRow[],
  currentIndex: number,
  key: TreeNavigationKey,
): number {
  const direction = key === 'ArrowUp' || key === 'End' ? -1 : 1;
  let candidateIndex = currentIndex + direction;
  if (key === 'Home') candidateIndex = 0;
  if (key === 'End') candidateIndex = visibleRows.length - 1;

  while (candidateIndex >= 0 && candidateIndex < visibleRows.length) {
    if (!visibleRows[candidateIndex]?.isDisabled) return candidateIndex;
    candidateIndex += direction;
  }

  return currentIndex;
}

function isTreeNavigationKey(key: string): key is TreeNavigationKey {
  return key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End';
}

export function getVirtualTreeRange(range: Range, activeIndex: number): number[] {
  const rangeIndexes = defaultRangeExtractor(range);
  if (activeIndex < 0 || rangeIndexes.includes(activeIndex)) return rangeIndexes;
  return [...rangeIndexes, activeIndex].sort((leftIndex, rightIndex) => leftIndex - rightIndex);
}

export function useTreeSelectVirtualization(visibleRows: readonly VisibleTreeRow[]): TreeSelectVirtualization {
  const parentRef = useRef<HTMLElement | null>(null);
  const pendingFocusPathRef = useRef<string | null>(null);
  const [activePath, setActivePath] = useState<string>();
  const shouldVirtualize = visibleRows.length > VIRTUALIZATION_THRESHOLD;
  const activePathIndex = activePath
    ? visibleRows.findIndex(visibleRow => visibleRow.properties.path === activePath && !visibleRow.isDisabled)
    : -1;
  const activeIndex = activePathIndex >= 0 ? activePathIndex : getNextTreeRowIndex(visibleRows, -1, 'ArrowDown');

  const getItemKey = useCallback((index: number) => visibleRows[index]?.properties.path ?? index, [visibleRows]);
  const rangeExtractor = useCallback((range: Range) => getVirtualTreeRange(range, activeIndex), [activeIndex]);

  const virtualizer = useVirtualizer({
    count: shouldVirtualize ? visibleRows.length : 0,
    getScrollElement: () => parentRef.current,
    getItemKey,
    estimateSize: () => 64,
    overscan: 8,
    rangeExtractor,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const tabbableIndex = activeIndex;

  const registerVirtualRow = useCallback(
    (index: number, node: Element | null) => {
      virtualizer.measureElement(node);

      const rowPath = visibleRows[index]?.properties.path;
      if (!node || !rowPath || pendingFocusPathRef.current !== rowPath) return;
      const rowElement = node.querySelector<HTMLElement>('[role="checkbox"], [role="option"]');
      if (!rowElement) return;
      pendingFocusPathRef.current = null;
      rowElement.focus();
    },
    [virtualizer, visibleRows],
  );

  const focusTreeRow = useCallback(
    (index: number) => {
      const rowPath = visibleRows[index]?.properties.path;
      if (!rowPath) return;
      pendingFocusPathRef.current = rowPath;
      setActivePath(rowPath);
      if (shouldVirtualize) virtualizer.scrollToIndex(index, { align: 'auto' });

      const mountedRow = parentRef.current?.querySelector<HTMLElement>(`[data-tree-row-index="${index}"]`);
      const rowElement = mountedRow?.querySelector<HTMLElement>('[role="checkbox"], [role="option"]');
      if (!rowElement) return;
      pendingFocusPathRef.current = null;
      rowElement.focus();
    },
    [shouldVirtualize, virtualizer, visibleRows],
  );

  const handleVirtualListKeyDown = useCallback<KeyboardEventHandler<HTMLElement>>(
    event => {
      if (!isTreeNavigationKey(event.key)) return;
      const rowContainer =
        event.target instanceof Element ? event.target.closest<HTMLElement>('[data-tree-row-index]') : null;
      const parsedIndex = Number(rowContainer?.dataset.treeRowIndex);
      const currentIndex = Number.isInteger(parsedIndex) ? parsedIndex : tabbableIndex;
      const nextIndex = getNextTreeRowIndex(visibleRows, currentIndex, event.key);

      event.preventDefault();
      focusTreeRow(nextIndex);
    },
    [tabbableIndex, visibleRows, focusTreeRow],
  );

  const handleVirtualRowFocus = useCallback(
    (index: number) => {
      const rowPath = visibleRows[index]?.properties.path;
      if (rowPath) setActivePath(rowPath);
    },
    [visibleRows],
  );

  const virtualizationState: TreeSelectVirtualizationState = shouldVirtualize
    ? { shouldVirtualize: true, virtualItems, totalSize: virtualizer.getTotalSize() }
    : { shouldVirtualize: false, virtualItems: EMPTY_VIRTUAL_ITEMS };

  return {
    parentRef,
    tabbableIndex,
    handleVirtualRowFocus,
    handleVirtualListKeyDown,
    registerVirtualRow,
    ...virtualizationState,
  };
}
